import type { z } from 'zod';

export type AdminV2ErrorShape = {
  code: string;
  message: string;
  fieldPath: string | null;
  blocking: boolean;
  details: Record<string, unknown> | null;
};

export class AdminV2Error extends Error {
  readonly code: string;
  readonly fieldPath: string | null;
  readonly blocking: boolean;
  readonly details: Record<string, unknown> | null;
  readonly status: number;

  constructor(params: {
    code: string;
    message: string;
    cause?: unknown;
    fieldPath?: string | null;
    blocking?: boolean;
    details?: Record<string, unknown> | null;
    status?: number;
  }) {
    super(params.message, { cause: params.cause });
    this.name = 'AdminV2Error';
    this.code = params.code;
    this.fieldPath = params.fieldPath ?? null;
    this.blocking = params.blocking ?? true;
    this.details = params.details ?? null;
    this.status = params.status ?? 400;
  }

  toShape(): AdminV2ErrorShape {
    return {
      code: this.code,
      message: this.message,
      fieldPath: this.fieldPath,
      blocking: this.blocking,
      details: this.details,
    };
  }
}

export function createAdminV2Error(params: {
  code: string;
  message: string;
  cause?: unknown;
  fieldPath?: string | null;
  blocking?: boolean;
  details?: Record<string, unknown> | null;
  status?: number;
}) {
  return new AdminV2Error(params);
}

function formatZodPath(path: PropertyKey[]) {
  if (path.length === 0) {
    return '$';
  }

  return path
    .map((segment) =>
      typeof segment === 'number' ? `[${segment}]` : String(segment)
    )
    .join('.')
    .replace(/\.\[/g, '[');
}

export function createAdminV2ValidationError(error: z.ZodError) {
  const firstIssue = error.issues[0];

  return createAdminV2Error({
    code: 'ADMIN_V2_VALIDATION_FAILED',
    message: firstIssue?.message ?? 'Invalid admin v2 request',
    fieldPath: firstIssue ? formatZodPath(firstIssue.path) : null,
    details: {
      issues: error.issues.slice(0, 10).map((issue) => ({
        code: issue.code,
        message: issue.message,
        fieldPath: formatZodPath(issue.path),
      })),
    },
    status: 400,
  });
}

export function getAdminV2ErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

export function getAdminV2PublicErrorMessage(error: unknown) {
  return error instanceof AdminV2Error
    ? error.message
    : 'Internal server error';
}

export function normalizeAdminV2Error(error: unknown): AdminV2Error {
  if (error instanceof AdminV2Error) {
    return error;
  }

  if (error instanceof Error) {
    return createAdminV2Error({
      code: 'ADMIN_V2_INTERNAL_ERROR',
      message: 'Internal server error',
      status: 500,
    });
  }

  return createAdminV2Error({
    code: 'ADMIN_V2_INTERNAL_ERROR',
    message: 'Internal server error',
    status: 500,
  });
}
